import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Select } from 'antd';
import { isEmpty } from 'lodash';
import { resolveCampaignDateRange } from '../../../../../../../core/utils/campaignDateRange';
import moment from 'moment-timezone';

import TopBar from './TopBar';
import SignUpsTable from './SignUpsTable';
import SignUpsChart from './SignUpsChart';
import CampaignRangeSelector from '../../../components/CampaignRangeSelector';
import { signUpsResponseData, groupByKeys } from './utils';
import { MenuItemTextColor } from '../../../../../../shared/globalStyling/styledText';
import { attributionWindowSelections } from '../../../../../../../core/utils/constants/constants';
import { useTimezone } from '../../../../../../../core/hooks/useTimezone';

const { Option } = Select;

const SignUpsLayout = ({
  selectedCampaign,
  campaignOrder,
  colStyle,
  chartData,
  bySignUpRangeDate,
  setSignUpRangeDate,
  dataSignUpRangeDate,
  setDataSignUpRangeDate,
  setbySignUpsAttributionWindow,
  setdataSignUpsDate,
  reportName,
  signupsTable,
  signupsTableLoading,
  setCurrentIdx,
  setCurrentStartEndDate,
  attributionWindow,
  setAttributionWindow,
}) => {
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();

  // Helper function to create timezone-aware moment
  const toTimezoneMoment = useCallback(
    (date, format) => {
      if (format) {
        return effectiveTimezone === 'UTC'
          ? moment.utc(date, format)
          : moment.tz(date, format, effectiveTimezone);
      }
      return effectiveTimezone === 'UTC'
        ? moment.utc(date)
        : moment.tz(date, effectiveTimezone);
    },
    [effectiveTimezone]
  );

  //Sets up variables
  const { startDate, endDate } = selectedCampaign ? selectedCampaign : [];

  const [currentPage, setCurrentPage] = useState(1);

  let campaignSelected = selectedCampaign ? selectedCampaign : [];
  const { signupsWithImpressions, hasSimplifiedReport } = chartData;

  const [signupsData, setSignupsData] = useState(
    chartData && chartData.signupsWithImpressions && chartData.signupsWithImpressions.length
      ? chartData.signupsWithImpressions
      : []
  );

  const isSimplified =
    chartData && chartData.hasSimplifiedReport ? chartData.hasSimplifiedReport : false;

  const attributionChange = useCallback(
    (selectedAttributionWindow) => {
      let detailBarFiltered = {
        startDate,
        endDate,
        signupsTotalCount: 0,
      };

      // Filtering of signups data by attribution
      const filteredSignupsWithAttribution =
        chartData &&
        chartData.signupsWithImpressions &&
        chartData.signupsWithImpressions.filter((signup) => {
          const latestDate = signup.latestDate
            ? signup.latestDate
            : signup.impressions &&
              signup.impressions.reduce((prev, current) => {
                const prevDate = new Date(prev.IMPRESSION_TIME);
                const currentDate = new Date(current.IMPRESSION_TIME);
                return prevDate > currentDate ? prev : current;
              }).IMPRESSION_TIME;

          const { signups_tstamp } = signup;
          const impressionDate = new Date(moment(latestDate).format());
          const outOfRange = new Date(moment(signups_tstamp).format());
          outOfRange.setDate(outOfRange.getDate() - selectedAttributionWindow);
          if (impressionDate < outOfRange) {
            return false;
          } else if (impressionDate > outOfRange) {
            return true;
          }
          return false;
        });

      const filteredSignups = filteredSignupsWithAttribution.filter((signup) => {
        return (
          toTimezoneMoment(signup.signups_tstamp).format('YYYY/MM/DD') >=
            toTimezoneMoment(startDate, 'YYYY/MM/DD').format('YYYY/MM/DD') &&
          toTimezoneMoment(signup.signups_tstamp).format('YYYY/MM/DD') <=
            toTimezoneMoment(endDate, 'YYYY/MM/DD').format('YYYY/MM/DD')
        );
      });

      //Generate a new by date object since there is a new set transactions due to attribution change
      const attributedByDate = [];
      filteredSignups.forEach((s) => {
        const latestDate = s.latestDate
          ? s.latestDate
          : s.impressions &&
            s.impressions.reduce((prev, current) => {
              const prevDate = new Date(prev.IMPRESSION_TIME);
              const currentDate = new Date(current.IMPRESSION_TIME);
              return prevDate > currentDate ? prev : current;
            }).IMPRESSION_TIME;

        const date = toTimezoneMoment(new Date(s.signups_tstamp)).format('MM/DD/YYYY');

        if (attributedByDate.find((c) => c.xAxis === date)) {
          const idx = attributedByDate.findIndex((c) => c.xAxis === date);
          attributedByDate[idx].signups_tstamp = date;
          attributedByDate[idx].count += 1;
          detailBarFiltered.signupsTotalCount += s.totalImpressions;
        } else {
          attributedByDate.push({
            signups_tstamp: date,
            latestDate: latestDate
              ? latestDate
              : s.impressions.reduce((acc, curr) => {
                  if (moment(curr.IMPRESSION_TIME)) {
                    return moment(curr.IMPRESSION_TIME) > moment(acc.IMPRESSION_TIME) ? curr : acc;
                  }
                  return acc;
                }).IMPRESSION_TIME,
            count: 1,
            xAxis: date,
          });

          detailBarFiltered.signupsTotalCount += s.totalImpressions;

          setDataSignUpRangeDate(attributedByDate);
          setSignupsData(filteredSignups);
          setdataSignUpsDate(attributedByDate);
          setbySignUpsAttributionWindow(selectedAttributionWindow);
          setAttributionWindow(selectedAttributionWindow);
          setCurrentIdx(0);
          setCurrentPage(1);
        }
      });
    },
    [
      chartData,
      startDate,
      endDate,
      setDataSignUpRangeDate,
      setSignupsData,
      setdataSignUpsDate,
      setbySignUpsAttributionWindow,
      setAttributionWindow,
      setCurrentIdx,
      setCurrentPage,
      toTimezoneMoment,
    ]
  );

  // Set Initial Report data
  useEffect(() => {
    if (chartData && chartData.signupsWithImpressions && chartData.signupsWithImpressions.length) {
      attributionChange(attributionWindow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributionWindow, chartData]);

  useEffect(() => {
    if (bySignUpRangeDate && bySignUpRangeDate.length) {
      let detailBarFiltered = {
        startDate,
        endDate,
        signupsTotalCount: 0,
      };

      // Filtering of signups data by attribution
      const filteredSignupsWithAttribution =
        chartData &&
        chartData.signupsWithImpressions &&
        chartData.signupsWithImpressions.filter((signup) => {
          const latestDate = signup.latestDate
            ? signup.latestDate
            : signup.impressions &&
              signup.impressions.reduce((prev, current) => {
                const prevDate = new Date(prev.IMPRESSION_TIME);
                const currentDate = new Date(current.IMPRESSION_TIME);
                return prevDate > currentDate ? prev : current;
              }).IMPRESSION_TIME;

          const { signups_tstamp } = signup;
          const impressionDate = new Date(moment(latestDate).format());
          const outOfRange = new Date(moment(signups_tstamp).format());
          outOfRange.setDate(outOfRange.getDate() - attributionWindow);
          if (impressionDate < outOfRange) {
            return false;
          } else if (impressionDate > outOfRange) {
            return true;
          }
          return false;
        });

      const filteredSignups = filteredSignupsWithAttribution.filter((signup) => {
        return (
          toTimezoneMoment(signup.signups_tstamp).format('YYYY/MM/DD') >=
            toTimezoneMoment(bySignUpRangeDate[0], 'YYYY/MM/DD').format('YYYY/MM/DD') &&
          toTimezoneMoment(signup.signups_tstamp).format('YYYY/MM/DD') <=
            toTimezoneMoment(bySignUpRangeDate[1], 'YYYY/MM/DD').format('YYYY/MM/DD')
        );
      });

      const attributedByDate = [];
      filteredSignups.forEach((s) => {
        const latestDate = s.latestDate
          ? s.latestDate
          : s.impressions &&
            s.impressions.reduce((prev, current) => {
              const prevDate = new Date(prev.IMPRESSION_TIME);
              const currentDate = new Date(current.IMPRESSION_TIME);
              return prevDate > currentDate ? prev : current;
            }).IMPRESSION_TIME;

        const date = toTimezoneMoment(new Date(s.signups_tstamp)).format('MM/DD/YYYY');

        if (attributedByDate.find((c) => c.xAxis === date)) {
          const idx = attributedByDate.findIndex((c) => c.xAxis === date);
          attributedByDate[idx].signups_tstamp = date;
          attributedByDate[idx].count += 1;
          detailBarFiltered.signupsTotalCount += s.totalImpressions;
        } else {
          attributedByDate.push({
            signups_tstamp: date,
            latestDate: latestDate
              ? latestDate
              : s.impressions.reduce((acc, curr) => {
                  if (moment(curr.IMPRESSION_TIME)) {
                    return moment(curr.IMPRESSION_TIME) > moment(acc.IMPRESSION_TIME) ? curr : acc;
                  }
                  return acc;
                }).IMPRESSION_TIME,
            count: 1,
            xAxis: date,
          });

          detailBarFiltered.signupsTotalCount += s.totalImpressions;
        }
      });
      setDataSignUpRangeDate(attributedByDate);
      setSignupsData(filteredSignups);
      setdataSignUpsDate(attributedByDate);
      setCurrentIdx(0);
      setCurrentPage(1);
    }
  }, [
    attributionWindow,
    bySignUpRangeDate,
    chartData,
    endDate,
    setCurrentIdx,
    setDataSignUpRangeDate,
    setdataSignUpsDate,
    startDate,
    toTimezoneMoment,
  ]);
  // Chart rows are computed once here so both the Performance-head picker
  // (data-extent for `defaultDateRange`) and `<SignUpsChart/>` body share
  // the same source of truth. Same expression that was previously inline
  // in the JSX `data={…}` prop.
  const chartRows = useMemo(() => {
    if (isEmpty(signupsWithImpressions)) return [];
    return signUpsResponseData(
      groupByKeys(
        signupsWithImpressions.map((signUp) => ({
          ...signUp,
          dateChart: moment(new Date(signUp.signups_tstamp)).format('YYYY-MM-DD'),
        })),
        ['dateChart']
      )[0]
    ).sort((a, b) => {
      if (a.xAxis === b.xAxis) return 0;
      if (moment(a.xAxis).isAfter(b.xAxis)) return 1;
      return -1;
    });
  }, [signupsWithImpressions]);

  // Data extent → picker `defaultDateRange`. Falls back to campaign flight
  // dates when the chart array is empty (matches V2's fallback chain).
  // Campaign flight start → today (see core/utils/campaignDateRange).
  // `campaignOrder`, NOT `selectedCampaign` — the latter is provider-scoped, so
  // anchoring to it would move the picker to that DSP's own flight start when
  // the provider changes.
  const defaultDateRange = useMemo(() => {
    return resolveCampaignDateRange(
      campaignOrder,
      (chartRows || []).map((row) => row && row.xAxis)
    );
  }, [chartRows, campaignOrder]);

  const [rangeType, setRangeType] = useState('Custom');

  // Picker handler — preserves the V1 DateSelector.onChange logic verbatim:
  // computes inclusive [start, end+1day] day-string + unix bounds, filters
  // chartRows for the range, then writes through to all parent setters.
  const handleRangeChange = (v) => {
    if (chartRows.length === 0) return;
    const dataStart = chartRows[0].xAxis;
    const dataEnd = chartRows[chartRows.length - 1].xAxis;

    const getSelectedStartDate = toTimezoneMoment(v ? v[0] : dataStart).format('YYYY-MM-DD');
    const unixSelectedStartDate = toTimezoneMoment(v ? v[0] : dataStart).unix();

    const getSelectedEndDate = toTimezoneMoment(v ? v[1] : dataEnd)
      .add(+1, 'days')
      .format('YYYY-MM-DD');
    const unixSelectedEndDate = toTimezoneMoment(v ? v[1] : dataEnd)
      .add(+1, 'days')
      .unix();

    const getFilteredData = chartRows.filter(
      (obj) => obj.xAxis >= getSelectedStartDate && obj.xAxis <= getSelectedEndDate
    );

    setSignUpRangeDate(v);
    setDataSignUpRangeDate(getFilteredData);
    setCurrentStartEndDate([unixSelectedStartDate, unixSelectedEndDate]);
    setCurrentPage(1);
    setCurrentIdx(0);
  };

  return (
    <div className="mjl-ds campaign-signups-v1">
      {/* Performance section — KPI strip (TopBar) + trend chart. The range
          picker lives top-right of this section's head, matching the V2
          Performance pattern (Summary / Display / SignUps V2). Picker uses
          segmented preset pills via `<CampaignRangeSelector/>`. */}
      <section className="campaign-overview__performance">
        <header className="campaign-overview__performance-head">
          <div className="campaign-overview__performance-eyebrow mono-eyebrow">
            Performance
          </div>
          {!isEmpty(signupsWithImpressions) && defaultDateRange && (
            <CampaignRangeSelector
              rangeType={rangeType}
              setRangeType={setRangeType}
              byCampaignRangeDate={bySignUpRangeDate}
              setByCampaignRangeDate={handleRangeChange}
              timezone={effectiveTimezone}
              defaultDateRange={defaultDateRange}
            />
          )}
        </header>

        <TopBar
          transactions={signupsData.length || 0}
          startDate={startDate}
          endDate={endDate}
        />

        {!isEmpty(signupsWithImpressions) && (
          <SignUpsChart
            data={chartRows}
            dataSignUpRangeDate={dataSignUpRangeDate}
          />
        )}
      </section>

      {/* Sign Ups section — attribution Select in head, DataTable below.
          Uses the shared `attribution-*` BEM from `_overview.scss` so the
          control gets the same label-over-Select layout + responsive
          stretch behavior as V1/V2 Transactions. The legacy
          `creatives-controls` BEM was scoped under `.campaign-transactions-
          v1` only and didn't apply here, leaving the label + Select
          touching with no gap. */}
      <section className="campaign-overview__creatives">
        <header className="campaign-overview__creatives-head">
          <div className="campaign-overview__creatives-eyebrow mono-eyebrow">Sign Ups</div>
          <div className="campaign-overview__attribution-controls">
            <label className="campaign-overview__attribution-field">
              <span className="campaign-overview__attribution-label mono-eyebrow">
                Attribution window
              </span>
              <Select
                value={attributionWindow}
                placeholder="Please select days"
                className="campaign-overview__attribution-select"
                popupClassName="mjl-ds"
                getPopupContainer={(trigger) => trigger.parentNode}
                size="small"
                onChange={(val) => {
                  attributionChange(val);
                }}
              >
                {attributionWindowSelections.map((option) => (
                  <Option key={option.key} value={option.key}>
                    <MenuItemTextColor text={option.name} />
                  </Option>
                ))}
              </Select>
            </label>
          </div>
        </header>

        <div className="campaign-overview__creatives-body">
          <div className="campaign-overview__creatives-table">
            <SignUpsTable
              signUpsData={
                !isSimplified ? signupsData || [] : signupsTable ? signupsTable.record || [] : []
              }
              signupTotalRecord={isSimplified && signupsTable ? signupsTable.count : 0}
              hasSimplifiedReport={hasSimplifiedReport}
              isSimplified={isSimplified}
              setCurrentIdx={setCurrentIdx}
              loading={signupsTableLoading}
              reportName={reportName}
              campaignOrderId={selectedCampaign && selectedCampaign.id ? selectedCampaign.id : null}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignUpsLayout;
